import { clsx } from 'clsx'

import { Button } from 'components/ui/Button'
import { FileDropzone } from 'components/ui/FileDropzone'
import { IconButton } from 'components/ui/IconButton'
import { ProgressBar } from 'components/ui/ProgressBar'
import { TextArea } from 'components/ui/TextArea'
import { ACCEPTED_VIDEO_UPLOAD_TYPES } from 'constants/videoUploadTypes'
import styles from 'modules/coaching/components/VideoAgentScreen.module.css'

import type { FormEvent, KeyboardEvent, ReactNode } from 'react'

type ConversationComposerUploadControl = {
  attachedVideoCount: number
  isDisabled: boolean
  isUploading: boolean
  maxFiles?: number
  onFilesDropped: (files: File[]) => void
  testId: string
}

type ConversationComposerProperties = {
  cancelLabel?: string
  dataTestId: string
  error: string | undefined
  formClassName?: string
  inputLoadingContent?: ReactNode
  isCancelVisible?: boolean
  isCancelDisabled?: boolean
  isInputDisabled: boolean
  isSubmitDisabled: boolean
  isSubmitLoading?: boolean
  onCancel?: () => void
  cancelTestId?: string
  onInput: (event: FormEvent<HTMLTextAreaElement>) => void
  onInputChange: (value: string) => void
  onInputKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  onSubmit: (event?: FormEvent) => Promise<void>
  placeholder: string
  progressPercentage: number | undefined
  statusText?: string
  submitTestId: string
  uploadControl?: ConversationComposerUploadControl
  value: string
  videoCount?: number
}

const getVideoAttachmentLabel = (attachedVideoCount: number): string => {
  return attachedVideoCount === 1 ? '1 video attached' : `${attachedVideoCount} videos attached`
}

export const ConversationComposer = ({
  cancelLabel = 'Cancel queued message',
  dataTestId,
  error,
  formClassName,
  inputLoadingContent,
  isCancelVisible = false,
  isCancelDisabled = false,
  isInputDisabled,
  isSubmitDisabled,
  isSubmitLoading = false,
  onCancel,
  cancelTestId,
  onInput,
  onInputChange,
  onInputKeyDown,
  onSubmit,
  placeholder,
  progressPercentage,
  statusText,
  submitTestId,
  uploadControl,
  value,
  videoCount,
}: ConversationComposerProperties) => {
  /* --- Section 3: Derived Values --- */
  const uploadButtonLabel = uploadControl?.isUploading ? 'Uploading' : 'Add video'
  const attachmentCount = uploadControl?.attachedVideoCount ?? videoCount
  const hasComposerStatus = progressPercentage !== undefined || Boolean(error)
  const shouldShowCancel = isCancelVisible && onCancel !== undefined
  const inputContent = inputLoadingContent ?? (
    <TextArea
      data-testid={dataTestId}
      value={value}
      onChange={event => onInputChange(event.target.value)}
      onInput={onInput}
      onKeyDown={onInputKeyDown}
      placeholder={placeholder}
      disabled={isInputDisabled}
      className={styles.videoAgentInput}
      rows={1}
    />
  )

  /* --- Section 6: Render --- */
  return (
    <form onSubmit={onSubmit} className={clsx(styles.videoAgentInputForm, formClassName)}>
      <div className={styles.videoAgentComposerPane}>
        <div className={styles.videoAgentInputWrapper}>{inputContent}</div>
        <div className={styles.videoAgentComposerActions}>
          <div className={styles.videoAgentComposerActionGroup}>
            {uploadControl ? (
              <FileDropzone
                accept={ACCEPTED_VIDEO_UPLOAD_TYPES}
                activeClassName={styles.videoAgentVideoUploadButtonActive}
                className={styles.videoAgentVideoUploadButton}
                disabledClassName={styles.videoAgentVideoUploadButtonDisabled}
                isDisabled={uploadControl.isDisabled}
                maxFiles={uploadControl.maxFiles}
                onFilesDropped={uploadControl.onFilesDropped}
                testId={uploadControl.testId}
              >
                {({ isDragActive }) => (
                  <>
                    <span className={styles.videoAgentVideoUploadIcon} aria-hidden="true">
                      +
                    </span>
                    <span className={styles.videoAgentVideoUploadText}>
                      {isDragActive ? 'Drop video' : uploadButtonLabel}
                    </span>
                  </>
                )}
              </FileDropzone>
            ) : undefined}
            {statusText || attachmentCount !== undefined ? (
              <div className={styles.videoAgentComposerMetaGroup}>
                {statusText ? <span className={styles.videoAgentComposerStatusLabel}>{statusText}</span> : undefined}
                {attachmentCount === undefined ? undefined : (
                  <span className={styles.videoAgentComposerAttachmentStatus}>
                    {getVideoAttachmentLabel(attachmentCount)}
                  </span>
                )}
              </div>
            ) : undefined}
          </div>
          {shouldShowCancel ? (
            <IconButton
              className={styles.videoAgentCancelBtn}
              data-testid={cancelTestId}
              disabled={isCancelDisabled}
              label={cancelLabel}
              onClick={onCancel}
              size="sm"
              title={cancelLabel}
            >
              ×
            </IconButton>
          ) : (
            <Button
              aria-label="Send message"
              data-testid={submitTestId}
              iconOnly
              isLoading={isSubmitLoading}
              disabled={isSubmitDisabled}
              className={styles.videoAgentSendBtn}
              type="submit"
            >
              <span aria-hidden="true">↑</span>
            </Button>
          )}
        </div>
        {hasComposerStatus ? (
          <div className={styles.videoAgentComposerStatus}>
            {progressPercentage === undefined ? undefined : (
              <ProgressBar className={styles.videoAgentVideoUploadProgress} value={progressPercentage} />
            )}
            {error ? <div className={styles.videoAgentVideoUploadError}>{error}</div> : undefined}
          </div>
        ) : undefined}
      </div>
    </form>
  )
}
